<template>
    <div class="reports">
        <v-btn-toggle
            v-model="selectedTimeOption"
            class="wfull mt-3"
            mandatory
        >
            <v-btn
                value="today"
                class="flex-1"
                color="primary"
                text
                :disabled="!selectedReport || !selectedReport.timeOptions.includes('today')"
            >
                {{ $t('general.today') }}
            </v-btn>
            <v-btn
                value="yesterday"
                class="flex-1"
                color="primary"
                text
                :disabled="!selectedReport || !selectedReport.timeOptions.includes('yesterday')"
            >
                {{ $t('general.yesterday') }}
            </v-btn>
            <v-btn
                value="byDate"
                class="flex-1"
                color="primary"
                text
                :disabled="!selectedReport || !isOnline || !selectedReport.timeOptions.includes('byDate')"
            >
                {{ $t('general.by_date') }}
            </v-btn>
        </v-btn-toggle>

        <v-select
            v-model="selectedReport"
            class="mt-5 mb-7"
            :items="filteredReports"
            item-text="name"
            return-object
            :label="$t('reports.report_type')"
            clearable
            hide-details
            clear-icon="fal fa-times"
            :disabled="busy"
            :loading="busy"
        />

        <v-form v-if="selectedReport">
            <v-autocomplete
                v-if="admin"
                v-model="form.cashRegister"
                :items="cashRegisters"
                :item-text="item => item.getFullName()"
                item-value="id"
                clearable
                clear-icon="fal fa-times"
                :label="$t('general.cash_register')"
                :error-messages="form.$errors.cashRegister"
                :hint="form.cashRegister && selectedTimeOption === 'shift' && !shift ? $t('reports.error.no_open_shift') : ''"
                persistent-hint
                @blur="form.validate('cashRegister')"
                @input="delete form.$errors.cashRegister"
            />

            <v-autocomplete
                v-if="selectedReport.type == 'sales_by_product'"
                v-model="form.products"
                :items="products"
                item-text="name"
                item-value="id"
                multiple
                chips
                clearable
                clear-icon="fal fa-times"
                :label="$t('general.inventory_item')"
                :error-messages="form.$errors.products"
                @blur="form.validate('products')"
                @input="delete form.$errors.products"
            />

            <v-autocomplete
                v-if="selectedReport.type == 'sales_by_client'"
                v-model="form.client"
                :items="clients"
                item-value="id"
                item-text="name"
                clearable
                clear-icon="fal fa-times"
                :label="$tc('general.buyer', 1)"
                :error-messages="form.$errors.client"
                @blur="form.validate('client')"
                @input="delete form.$errors.client"
            />

            <v-row class="mt-1" v-if="selectedTimeOption === 'byDate'">
                <v-col class="pt-2 pb-0" cols="8">
                    <v-menu
                        v-model="picker1"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="290"
                    >
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                v-model="form.startDate"
                                :error-messages="form.$errors.startDate"
                                :label="$t('general.start_date')"
                                prepend-icon="fal fa-calendar"
                                :disabled="!!year"
                                readonly
                                v-on="on"
                            />
                        </template>
                        <v-date-picker
                            v-model="form.startDate"
                            :max="moment().format('YYYY-MM-DD')"
                            :disabled="!!year"
                            @input="picker1 = false; form.validate('startDate')"
                        />
                    </v-menu>
                </v-col>

                <v-col class="pt-2 pb-0" cols="4">
                    <v-text-field
                        v-model="form.startTime"
                        v-mask="'##:##:##'"
                        placeholder="hh:mm:ss"
                        :error-messages="form.$errors.startTime"
                        :label="$t('general.time')"
                        :disabled="!!year || (selectedReport && selectedReport.type === 'daily_report')"
                        @blur="form.validate('startTime')"
                        @input="delete form.$errors.startTime"
                    />
                </v-col>

                <v-col class="pt-2 pb-0" cols="8">
                    <v-menu
                        v-model="picker2"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="290"
                    >
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                v-model="form.endDate"
                                :error-messages="form.$errors.endDate"
                                :label="$t('general.end_date')"
                                :disabled="!!year || (selectedReport && selectedReport.type === 'daily_report')"
                                prepend-icon="fal fa-calendar"
                                readonly
                                v-on="on"
                            />
                        </template>
                        <v-date-picker
                            v-model="form.endDate"
                            :min="form.startDate"
                            :max="!form.startDate ? moment().format('YYYY-MM-DD') : moment(form.startDate).add(3, 'months').format('YYYY-MM-DD')"
                            :disabled="!!year"
                            @input="picker2 = false; form.validate('endDate')"
                        />
                    </v-menu>
                </v-col>

                <v-col class="pt-2 pb-0" cols="4">
                    <v-text-field
                        v-model="form.endTime"
                        v-mask="'##:##:##'"
                        placeholder="hh:mm:ss"
                        :error-messages="form.$errors.endTime"
                        :label="$t('general.time')"
                        :disabled="!!year || (selectedReport && selectedReport.type === 'daily_report')"
                        @blur="form.validate('endTime')"
                        @input="delete form.$errors.endTime"
                    />
                </v-col>

                <v-col
                    v-if="isOnline && selectedReport && (selectedReport.type == 'periodic_report' || selectedReport.type == 'total_sales')"
                    class="pt-2 pb-0"
                    cols="12"
                >
                    <v-select
                        v-model="year"
                        :items="availableYears"
                        :label="$t('reports.year_report')"
                        prepend-icon="fal fa-calendar-check"
                        clearable
                        clear-icon="fal fa-times"
                    />
                </v-col>
            </v-row>
        </v-form>

        <div class="print-btn">
            <v-btn
                color="primary"
                block
                depressed
                :disabled="!selectedReport || busy || (selectedTimeOption === 'shift' && !shift)"
                :loading="busy"
                @click="generateReport"
            >
                {{ $t('general.print') }}
            </v-btn>
        </div>
    </div>
</template>

<script>
import FiscalServiceLegacy from '@/services/FiscalServiceLegacy';
import EinvoiceService from '@/services/EinvoiceService';
import CashRegister from '@/models/CashRegister';
import Client from '@/models/Client';
import Product from '@/models/Product';
import Report from '@/models/Report';
import Shift from '@/models/Shift';
import { requiredIf, helpers } from 'vuelidate/lib/validators';

export default {
    props: {
        cashRegisters: {
            type: Array,
            default: () => CashRegister.all()
        },
        shifts: {
            type: Array,
            default: () => Shift.all()
        },
        clients: {
            type: Array,
            default: () => Client.all()
        },
        products: {
            type: Array,
            default: () => Product.all()
        },
        admin: {
            type: Boolean,
            default: false
        }
    },

    created() {
        this.form = this.createForm()
    },

    data() {
        return {
            busy: false,

            form: null,
            selectedReport: null,
            selectedTimeOption: 'today',
            timeOptions: ['today', 'yesterday', 'byDate', 'shift'],
            startTime: null,
            endTime: null,

            picker1: false,
            picker2: false,
            year: null
        }
    },

    validations() {
        const salesReportTypes = [
            'total_sales',
            'sales_by_client',
            'sales_by_user',
            'sales_by_product',
            'sales_by_product_category',
            'sales_by_product_type',
            'sales_by_product_type_sent_to_room',
            'sales_by_product_type_and_taxes',
            'sales_by_sector',
            'sales_by_ingredient'
        ]

        return {
            form: {
                cashRegister: { requiredIf: requiredIf(form => this.selectedReport && !salesReportTypes.includes(this.selectedReport.type)) },
                client: { requiredIf: requiredIf(form => this.selectedReport && this.selectedReport.type === 'sales_by_client') },
                products: { requiredIf: requiredIf(form => this.selectedReport && this.selectedReport.type === 'sales_by_product') },
                startDate: { requiredIf: requiredIf(form => this.selectedTimeOption === 'byDate') },
                startTime: {
                    requiredIf: requiredIf(form => this.selectedTimeOption === 'byDate'),
                    time: helpers.withParams(
                        { type: 'time' },
                        function(value) {
                            if(!value) return true

                            return moment(value, 'HH:mm:ss', true).isValid()
                        }
                    )
                },
                endDate: {
                    requiredIf: requiredIf(form => this.selectedTimeOption === 'byDate'),
                    maxDate: helpers.withParams(
                        { type: 'maxDate' },
                        function(value) {
                            if(!value || !this.form.startDate || this.year) return true

                            return moment(value).isSameOrBefore(moment(this.form.startDate).add(3, 'months'))
                        }
                    )
                },
                endTime: {
                    requiredIf: requiredIf(form => this.selectedTimeOption === 'byDate'),
                    timeAfter: helpers.withParams(
                        { type: 'timeAfter' },
                        function(value) {
                            if(!value) return true

                            if(!moment(value, 'HH:mm:ss', true).isValid()) {
                                return false
                            }

                            if(!this.form.startDate || !this.form.startTime || !this.form.endDate) {
                                return false
                            }

                            return moment(`${this.form.endDate} ${value}`)
                                .isAfter(moment(`${this.form.startDate} ${this.form.startTime}`))
                        }
                    )
                },
            }
        }
    },

    watch: {
        isOnline(val) {
            if(!val) {
                if(this.selectedTimeOption === 'byDate') {
                    this.selectedTimeOption = 'today'
                }

                this.selectedReport = null
            }
        },

        'form.startDate'(val) {
            this.form.startTime = val ? '00:00:00' : null
            this.form.endDate = null;
            this.form.endTime = null;

            if(val && this.selectedReport && this.selectedReport.type === 'daily_report') {
                this.form.endDate = val
            }

            this.form.validate('startTime')
            this.form.validate('endDate')
            this.form.validate('endTime')
        },

        'form.endDate'(val) {
            this.form.endTime = val ? '23:59:59' : null
            this.form.validate('endTime')
        },

        selectedReport(val) {
            this.selectedTimeOption = val ? val.timeOptions[0] : 'today'
            this.form = this.createForm()
            this.year = null
        },

        year(val) {
            if(val) {
                this.form.startDate = moment(val).startOf('year').format('YYYY-MM-DD')
                this.form.startTime = '00:00:00'
                this.$nextTick(() => {
                    this.form.endDate = moment(val).endOf('year').format('YYYY-MM-DD')
                    this.form.endTime = '23:59:59'
                })
            } else {
                this.form.startDate = this.form.startTime = null
                this.$nextTick(() => {
                    this.form.endDate = this.form.endTime = null
                })
            }

            this.$nextTick(() => {
                this.form.validate('startDate')
                this.form.validate('startTime')
                this.form.validate('endDate')
                this.form.validate('endTime')
            })
        }
    },

    computed: {
        shift() {
            const cashRegister = !this.admin ? CashRegister.current() : CashRegister.find(this.form.cashRegister)

            if(!cashRegister) return null

            return this.shifts.find(shift => !shift.closed_at && shift.cash_register_id === cashRegister.id)
        },

        reports() {
            return Report.types(this.admin)
        },

        filteredReports() {
            return this.reports.filter(report => report.enabled)
        },

        einvoiceEnabled() {
            return EinvoiceService.isEnabled();
        },

        einvoiceOnly() {
            return EinvoiceService.einvoiceOnly();
        },

        availableYears() {
            return Array.from({ length: 3 }, (v, k) => moment().subtract(k + 1, 'years').format('YYYY'));
        }
    },

    methods: {

        /**
         * Create report form
         */
        createForm() {
            const cashRegister = CashRegister.current()
            const cashRegisterId = !this.admin && cashRegister ? cashRegister.id : null

            return new Form(this, 'form', {
                cashRegister: cashRegisterId,
                client: null,
                products: [],
                startDate: null,
                startTime: null,
                endDate: null,
                endTime: null,
            })
        },

        /**
         * Generate report
         */
        async generateReport() {
            if(!this.form.validate()) return

            if(!this.selectedReport) return

            let startDateTime = null
            let endDateTime = null

            if(this.selectedTimeOption === 'today') {
                startDateTime = moment().startOf('day').format('YYYY-MM-DD HH:mm:ss')
                endDateTime = moment().format('YYYY-MM-DD') + ' 23:59:59'
            } else if(this.selectedTimeOption === 'yesterday') {
                startDateTime = moment().subtract(1, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss')
                endDateTime = moment().subtract(1, 'days').format('YYYY-MM-DD') + ' 23:59:59'
            } else if(this.selectedTimeOption === 'shift') {
                startDateTime = `${this.shift.getOpenedAt().format('YYYY-MM-DD HH:mm:ss')}`;
                endDateTime = `${moment().format('YYYY-MM-DD HH:mm:ss')}`;
            } else {
                startDateTime = this.form.startDate && this.form.startTime ? `${this.form.startDate} ${this.form.startTime}` : null;
                endDateTime = this.form.endDate && this.form.endTime ? `${this.form.endDate} ${this.form.endTime}` : null;
            }

            this.busy = true;

            if(this.selectedReport.category === 'legacy') {
                if(this.selectedReport.type === 'current_state_legacy') {
                    await FiscalServiceLegacy.printCurrentStateReport()
                } else if(this.selectedReport.type === 'daily_report_legacy') {
                    await FiscalServiceLegacy.printDailyReport()
                } else if(this.selectedReport.type === 'periodic_report_legacy') {
                    await FiscalServiceLegacy.printPeriodicReport({
                        from: moment(startDateTime).format('D. M. YYYY. HH:mm:ss'),
                        to: moment(endDateTime).format('D. M. YYYY. HH:mm:ss'),
                    })
                }

                this.busy = false;
                return
            }

            const report = await Report.create({
                type: this.selectedReport.type,
                width: this.selectedReport.width,
                startTime: startDateTime,
                endTime: endDateTime,
                year: this.year,
                cashRegisterId: this.form.cashRegister,
                orders: this.selectedTimeOption === 'shift' ? (this.shift?.getOrders() || []) : null,
                admin: this.admin,
                filterOrders: {
                    clientId: this.form.client,
                    cashRegisterId: this.form.cashRegister,
                    products: this.form.products
                }
            });

            this.busy = false;

            if(!report) {
                this.$snackbar(this.$t('reports.error.general'), 'error');
                return;
            }

            this.$emit('create', { report: report })
        },
    }
};
</script>

<style lang="scss" scoped>
.reports {
    .print-btn {
        position: absolute;
        bottom: 20px;
        left: 20px;
        right: 20px;
    }
}
</style>
